import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/user_management_styles.css";
import Navbar from "./Navbar.js";
import "bootstrap/dist/css/bootstrap.min.css";
import UserGroupTable from "./tables/UserGroupTable.js";
import GroupTable from "./tables/GroupTable.js";
import swal from "sweetalert";
import "../styles/form_styles.css";
import Axios from "axios";
import config from "../config.js";
import Cookie from "universal-cookie";
import { decrypt } from "./useCryptoJS";
import mixpanel from "mixpanel-browser";

const UserManagement_CreateUserGroup = () => {
  const cookies = new Cookie();
  const [userToken, setUserToken] = useState("");
  const [actionInitiatingUser, setActionInitiatingUser] = useState(-1);

  // State for permissions and UI control
  const [uruserGroup, setUruserGroup] = useState("0000");
  const [createPer, setCreatePer] = useState(false);

  // State for selected items
  const [selectedGroups, setSelectedGroups] = useState([]); // Updated to array
  const selectedUserIDsRef = useRef([]);
  const [selectedUserIDs, setSelectedUserIDs] = useState([]);
  const [highlightedUserIDs, setHighlightedUserIDs] = useState([]);

  // UI state
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Refs
  const groupNameRef = useRef(null);

  // Headers for API calls
  const headersRef = useRef({
    Authorization: `Bearer ${userToken}`,
    "Content-Type": "application/json",
  });

  // Initialize user token and ID
  useEffect(() => {
    try {
      const token = cookies.get("userToken");
      const userId = parseInt(cookies.get("userID"));
      setUserToken(token);
      setActionInitiatingUser(userId);
      headersRef.current = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    } catch (error) {
      console.log("Error reading cookies:", error);
    }
  }, []);

  // Initialize permissions
  useEffect(() => {
    try {
      const decryptedUserGroup = decrypt(cookies.get("uruserGroup"));
      setUruserGroup(decryptedUserGroup);
      setCreatePer(decryptedUserGroup.charAt(1) === "1");
    } catch (error) {
      console.log("Error decrypting user group:", error);
    }
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setIsEditMode(false);
    setSelectedGroups([]); // Updated
    setHighlightedUserIDs([]);
    setSelectedUserIDs([]);
    selectedUserIDsRef.current = [];
    if (groupNameRef.current) {
      groupNameRef.current.value = "";
    }
  }, []);

  // Handle cancel button
  const handleCancel = useCallback(
    (e) => {
      if (e) e.preventDefault();
      resetForm();
    },
    [resetForm]
  );

  // Handle group selection
  const handleGroupSelect = useCallback((selectedData) => {
    setSelectedGroups(selectedData || []); // Ensure array, handle null/undefined
  }, []);

  // Handle user selection
  const handleUserSelect = useCallback((userIds) => {
    const areUserIDsEqual =
      userIds.length === selectedUserIDsRef.current.length &&
      userIds.every((id, index) => id === selectedUserIDsRef.current[index]);

    if (!areUserIDsEqual) {
      selectedUserIDsRef.current = [...userIds];
      setSelectedUserIDs(userIds);
    }
  }, []);

  // Handle edit button
  const handleEdit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      mixpanel.track("User Group Edit Button Clicked", {
        userID: cookies.get("userID"),
      });

      if (selectedGroups.length !== 1) {
        swal(
          selectedGroups.length === 0
            ? "No Group Selected"
            : "Please select exactly one group to edit!",
          selectedGroups.length === 0 ? "Please select a group!" : "",
          "warning"
        );
        return;
      }

      const groupToEdit = selectedGroups[0];
      try {
        setIsSubmitting(true);
        const response = await Axios.get(
          `${config.BACKEND_URL}/usergroups/${groupToEdit.id}`,
          { headers: headersRef.current }
        );

        if (response.data) {
          if (groupNameRef.current) {
            groupNameRef.current.value = response.data.name;
          }
          const userIDs = response.data.userIDs || [];
          setHighlightedUserIDs([...userIDs]);
          setSelectedUserIDs([...userIDs]);
          selectedUserIDsRef.current = [...userIDs];
          setIsEditMode(true);
        }
      } catch (error) {
        console.log("Error fetching group details:", error);
        swal("Error", "Failed to fetch group details", "error");
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedGroups]
  );

  // Handle delete button
  const handleDelete = useCallback(
    (e) => {
      if (e) e.preventDefault();
      mixpanel.track("User Group Delete Button Clicked", {
        userID: cookies.get("userID"),
      });

      if (selectedGroups.length === 0) {
        swal("No Group Selected", "Please select at least one group!", "error");
        return;
      }

      swal({
        title: "Are you sure?",
        text: `Once deleted, you will not be able to recover these ${selectedGroups.length} group(s)!`,
        icon: "warning",
        buttons: {
          cancel: "Cancel",
          delete: { text: "Delete", value: "delete" },
        },
        dangerMode: true,
      }).then((value) => {
        if (value === "delete") {
          setIsSubmitting(true);
          Promise.all(
            selectedGroups.map((group) =>
              Axios.delete(
                `${config.BACKEND_URL}/usergroups/${group.id}?actionInitiatingUser=${actionInitiatingUser}`,
                { headers: headersRef.current }
              )
            )
          )
            .then(() => {
              swal("Groups deleted successfully!", "", "success");
              resetForm();
              setTimeout(() => window.location.reload(), 100);
            })
            .catch((err) => {
              console.log("Error deleting groups:", err);
              swal("Error", "Failed to delete groups", "error");
              setIsSubmitting(false);
            });
        } else {
          swal("Delete operation cancelled!", "", "info");
        }
      });
    },
    [selectedGroups, actionInitiatingUser, resetForm]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      mixpanel.track("User Group Submit Button Clicked", {
        userID: cookies.get("userID"),
      });

      const groupName = groupNameRef.current ? groupNameRef.current.value : "";
      if (!groupName) {
        swal("No group name entered!", "Process Failed", "error");
        return;
      }

      if (isEditMode) {
        if (selectedGroups.length !== 1) {
          swal(
            "Error",
            "Invalid state: multiple groups selected for edit",
            "error"
          );
          return;
        }
        const groupToUpdate = selectedGroups[0];
        swal({
          title: "Are you sure you want to update this group?",
          icon: "warning",
          buttons: {
            cancel: "Cancel",
            update: { text: "Update", value: "update" },
          },
        }).then((value) => {
          if (value === "update") {
            setIsSubmitting(true);
            Axios.patch(
              `${config.BACKEND_URL}/usergroups/${groupToUpdate.id}`,
              {
                name: groupName,
                userIDs: selectedUserIDsRef.current,
                actionInitiatingUser,
              },
              { headers: headersRef.current }
            )
              .then(() => {
                swal("Group updated successfully", "", "success");
                resetForm();
                setTimeout(() => window.location.reload(), 100);
              })
              .catch((err) => {
                console.log("Error updating group:", err);
                swal("Error", "Failed to update group", "error");
                setIsSubmitting(false);
              });
          } else {
            swal("Update operation cancelled!", "");
          }
        });
      } else {
        if (
          !selectedUserIDsRef.current ||
          selectedUserIDsRef.current.length === 0
        ) {
          swal("No users selected!", "Process Failed", "error");
          return;
        }
        setIsSubmitting(true);
        Axios.post(
          `${config.BACKEND_URL}/usergroups`,
          {
            groupName,
            userIDs: selectedUserIDsRef.current,
            actionInitiatingUser,
          },
          { headers: headersRef.current }
        )
          .then(() => {
            swal("Group created successfully", "", "success");
            resetForm();
            setTimeout(() => window.location.reload(), 100);
          })
          .catch((err) => {
            console.log("Error creating group:", err);
            swal("Group Already Exists", "Process Failed", "error");
            setIsSubmitting(false);
          });
      }
    },
    [isEditMode, selectedGroups, actionInitiatingUser, resetForm]
  );

  return (
    <div className="UserManagement_CreateUserGroup pt-2">
      <link
        href="https://fonts.googleapis.com/icon?family=Material+Icons"
        rel="stylesheet"
      />
      <Navbar />
      <main className="container-fluid" style={{ textAlign: "center" }}>
        <header className="h2 mt-3">User Group Management</header>
        <section className="row">
          {/* Users Section */}
          <div
            className="col-sm-4 p-2 justify-content-center"
            style={{ height: "auto" }}
          >
            <div>
              <header className="h3 text-center mb-1">All Users</header>
              <section
                className="d-flex row justify-content-center"
                style={{ width: "100%", float: "left" }}
              >
                <UserGroupTable
                  setSelectedUserRows={handleUserSelect}
                  highlightedUserIDs={highlightedUserIDs}
                />
              </section>
            </div>
          </div>

          {/* Form Section */}
          <div
            className="col-sm-4 p-2 d-flex justify-content-center"
            style={{ height: "auto" }}
          >
            {uruserGroup.charAt(1) === "1" && (
              <form
                onSubmit={handleSubmit}
                className="d-flex flex-column justify-content-center align-items-center"
              >
                <p className="h3 mb-0">
                  {isEditMode ? "Edit User Group" : "Create User Group"}
                </p>
                <div className="col-xs-4 col-xs-offset-4">
                  <div className="floating-label-group">
                    <input
                      type="text"
                      id="name"
                      className="form-control"
                      autoComplete="off"
                      required
                      style={{ fontWeight: "bold" }}
                      ref={groupNameRef}
                      disabled={isSubmitting}
                    />
                    <label className="floating-label">Group Name</label>
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    type="submit"
                    className="btn btn-success"
                    id="submit"
                    disabled={isSubmitting}
                  >
                    {isEditMode ? "Update" : "Submit"}
                  </button>{" "}
                   
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Groups Section */}
          <div
            className="col-sm-4 p-2 justify-content-center"
            style={{ height: "auto" }}
          >
            <div>
              <header className="h3 text-center mb-1">All Groups</header>
              <section
                className="d-flex row justify-content-center"
                style={{ width: "100%", float: "left" }}
              >
                {uruserGroup.charAt(0) === "1" && (
                  <GroupTable
                    setRowEventListener={() => {}}
                    onGroupSelect={handleGroupSelect}
                  />
                )}
                <section className="d-flex justify-content-center mt-2">
                  {uruserGroup.charAt(2) === "1" && (
                    <button
                      className="btn btn-primary d-flex align-items-center justify-content-center"
                      onClick={handleEdit}
                      disabled={isSubmitting}
                    >
                      Edit
                    </button>
                  )}
                         
                  {uruserGroup.charAt(3) === "1" && (
                    <button
                      className="btn btn-danger d-flex align-items-center justify-content-center"
                      onClick={handleDelete}
                      disabled={isSubmitting}
                    >
                      Delete
                    </button>
                  )}
                </section>
              </section>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default UserManagement_CreateUserGroup;
